/**
 * errorHandler.js — Centralized user-friendly error messages (French)
 *
 * Usage:
 *   import { getFriendlyError } from '../utils/errorHandler';
 *
 *   try { ... }
 *   catch (err) { setError(getFriendlyError(err)); }
 *
 * The function:
 *   1. Reads the HTTP status code from the error message (our API throws
 *      errors with messages like "401 — Email ou mot de passe incorrect.")
 *   2. Returns a clean French message — never exposes raw status codes
 *      or backend stack traces to the user.
 */

const STATUS_MESSAGES = {
  400: "Requête invalide. Veuillez vérifier vos informations.",
  401: "Email ou mot de passe incorrect.",
  403: "Vous n'avez pas accès à cette action.",
  404: "Ressource introuvable.",
  409: "Cette action est en conflit avec des données existantes.",
  422: "Veuillez corriger les champs du formulaire.",
  429: "Trop de tentatives. Veuillez patienter quelques instants.",
  500: "Erreur serveur. Veuillez réessayer plus tard.",
  502: "Le serveur est temporairement indisponible. Réessayez dans un moment.",
  503: "Service indisponible. Réessayez dans un moment.",
};

const DEFAULT_MESSAGE = "Une erreur est survenue. Veuillez réessayer.";

/**
 * Extract the HTTP status code from an Error object.
 * Our authContext throws errors whose .message starts with "NNN — ..."
 */
function extractStatus(err) {
  if (!err?.message) return null;
  const match = err.message.match(/^(\d{3})\s*[—-]/);
  if (match) return parseInt(match[1], 10);
  return null;
}

/**
 * Returns a user-friendly French error message.
 *
 * @param {Error|unknown} err
 * @param {Object} overrides  - Optional map of { statusCode: 'custom message' }
 *                              to override specific status codes per call site.
 * @returns {string}
 */
export function getFriendlyError(err, overrides = {}) {
  // Network / fetch failure
  if (err instanceof TypeError && err.message === 'Failed to fetch') {
    return "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
  }

  const status = extractStatus(err);

  if (status) {
    // Call-site override takes priority
    if (overrides[status]) return overrides[status];
    // Global status mapping
    if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  }

  // Fallback: if the error message itself looks safe to show (no code prefix),
  // return it — otherwise return the default.
  const msg = err?.message ?? '';
  const isSafeMessage = msg && !/^\d{3}\s*[—-]/.test(msg) && msg.length < 200;
  return isSafeMessage ? msg : DEFAULT_MESSAGE;
}
